import { ErrorHandler, NgModule, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
// Angular 17: Import standalone components directly
import { ResponsiveService } from '../core/responsive/responsive.service';
import { ThemeService } from '../core/theme/theme.service';
import { ResponsiveDirective } from '../core/responsive/responsive.directive';
import { AppShellComponent } from '../layout/app-shell/app-shell.component';
import { BaseCardComponent } from '../shared-ui/base-card/base-card.component';
import { ResponsiveGridComponent } from '../shared-ui/responsive-grid/responsive-grid.component';
import { MenuDesktopComponent } from './components/menu-desktop/menu-desktop.component';
import { MenuMovilComponent } from './components/menu-movil/menu-movil.component';
import { CardRealEstateComponent } from './components/card-real-estate/card-real-estate.component';
import { DetailRealEstateComponent } from './components/detail-real-estate/detail-real-estate.component';
import { AdminComponent } from './components/admin/admin.component';
import { UserInvestmentsComponent } from './components/user-investments/user-investments.component';
import { DocumentationComponent } from './components/documentation/documentation.component';
import { MainContainerComponent } from './components/main-container/main-container.component';
import { NavbarUserComponent } from './components/navbar-user/navbar-user.component';
import { SpinnerComponent } from './components/UI/spinner/spinner.component';
import { CardsRealEstateComponent } from './components/cards-real-estate/cards-real-estate.component';
import { LoginComponent } from './components/login/login.component';
import { KycComponent } from './components/kyc/kyc.component';
import { environment } from '../environments/environment';
import { ScreenTrackingService, UserTrackingService } from '@angular/fire/analytics';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { SafePipe } from './pipes/safe.pipe';
import { NewProyectComponent } from './components/new-proyect/new-proyect.component';
import { SetRolComponent } from './components/set-rol/set-rol.component';
import { NewSaleSellerComponent } from './components/new-sale-seller/new-sale-seller.component';
import { DocumentationGeneralComponent } from './components/documentation-general/documentation-general.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { SetDataSaleComponent } from './components/set-data-sale/set-data-sale/set-data-sale.component';
import { AddDividendsComponent } from './components/add-dividends/add-dividends/add-dividends.component';
import { ProyectsSetDataComponent } from './components/proyects-set-data/proyects-set-data/proyects-set-data.component';
import { ProyectSetDataComponent } from './components/proyect-set-data/proyect-set-data/proyect-set-data.component';
import { UriYoutubeComponent } from './components/uriYoutube/uri-youtube/uri-youtube.component';
import { EditTutorialsComponent } from './components/edit-tutorials/edit-tutorials.component';
import { EditReferralTutorialsComponent } from './components/edit-referral-tutorials/edit-referral-tutorials.component';
import { ProfileComponent } from './components/profile/profile/profile.component';
import { BuyThroughCompanyComponent } from './components/buy-through-company/buy-through-company/buy-through-company.component';
import { SetWhitelistComponent } from './components/set-whitelist/set-whitelist/set-whitelist.component';
import { ForcedTransferComponent } from './components/forced-transfer/forced-transfer/forced-transfer.component';
import { SetPauseComponent } from './components/set-pause/set-pause/set-pause.component';
import { SetKycComponent } from './components/set-kyc/set-kyc/set-kyc.component';
import { RevertPaymentsComponent } from './components/revert-payments/revert-payments/revert-payments.component';
import { InfoDividendsComponent } from './components/info-dividends/info-dividends/info-dividends.component';
import { BuyWithoutPayComponent } from './components/buy-without-pay/buy-without-pay/buy-without-pay.component';
import { HideProyectComponent } from './components/hide-proyect/hide-proyect/hide-proyect.component';
import { DocsLegalComponent } from './components/docs-legal/docs-legal/docs-legal.component';
import { ModalLoginComponent } from './components/modal-login/modal-login.component';
import { NgbModule, NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import { ModalRegisterComponent } from './components/modal-register/modal-register.component';
// MDB Angular UI Kit modules removed - using Angular Material equivalents
import { HammerModule } from '@angular/platform-browser';
import { ClickTouchDirective } from './click-touch.directive';
import { AuthService } from './../app/services/auth.service';
import { GlobalErrorHandlerService } from './services/global-error-handler.service';
import { ModalKycComponent } from './components/modal-kyc/modal-kyc.component';
import { MatDialogModule } from '@angular/material/dialog';
import { KycStagesComponent } from './components/modal-kyc/kyc-stages/kyc-stages.component';
import { KycRegisterComponent } from './components/modal-kyc/kyc-register/kyc-register.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { NgSelectModule } from '@ng-select/ng-select';
import { CustomDialogComponent } from './components/dialogs/custom-dialog/custom-dialog.component';
import { KycSurveyComponent } from './components/modal-kyc/kyc-survey/kyc-survey.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AdminWalletComponent } from './components/admin-wallet/admin-wallet.component';
import { KycCompletedComponent } from './components/modal-kyc/kyc-completed/kyc-completed.component';
import { SharedModule } from './components/shared/shared.module';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { WithdrawalRequestsComponent } from './components/withdrawal-requests/withdrawal-requests.component';
import { FailedPaymentsManagementComponent } from './components/failed-payments-management/failed-payments-management.component';
import { ReservesManagementComponent } from './components/reserves-management/reserves-management.component';
import { FailedPaymentsFiltersComponent } from './components/failed-payments-management/failed-payments-filters.component';
import { FailedPaymentsTableComponent } from './components/failed-payments-management/failed-payments-table.component';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { ModalPageComponent } from './components/modal-page/modal-page.component';
import { RegisterPageComponent } from './components/register-page/register-page.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { FiltersBarComponent } from './components/filters-bar/filters-bar.component';
import { KycFinalComponent } from './components/kyc-final/kyc-final.component';
import { MatIconModule } from '@angular/material/icon';
// Angular Material dialog components are included in MatDialogModule
import { MatOptionModule } from '@angular/material/core';
import { MatRippleModule } from '@angular/material/core';
import { InvestmentTutorialsComponent } from './components/investment-tutorials/investment-tutorials.component';
import { Verify2faComponent } from './components/auth/verify-2fa/verify-2fa.component';
import { TwoFactorAuthService } from './services/two-factor/two-factor-auth.service';
import { BuyBatchThroughCompanyComponent } from './components/buy-batch-through-company/buy-batch-through-company.component';
import { InvestmentListComponent } from './components/buy-batch-through-company/investment-list/investment-list.component';
import { TransferBatchComponent } from './components/transfer-batch/transfer-batch.component';
import { TransferListComponent } from './components/transfer-batch/transfer-list/transfer-list.component';
import { ManageBonusesComponent } from './components/manage-bonuses/manage-bonuses.component';
import { BalanceResumeComponent } from './components/balance-resume/balance-resume.component';
import { BalanceListComponent } from './components/balance-resume/balance-list/balance-list.component';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { MfaInterceptor } from './interceptors/mfa.interceptor';
import { RetryInterceptor } from './interceptors/retry.interceptor';
import { ResidenceChangeModalModule } from './components/my-profile/residence-change-modal/residence-change-modal.module';
import { AdminFiscalDocumentationComponent } from './components/admin/admin-fiscal-documentation/admin-fiscal-documentation.component';
import { SetCreditCardFeeComponent } from './components/set-credit-card-fee/set-credit-card-fee.component';

@NgModule({
  declarations: [
    AppComponent,
    CardRealEstateComponent,
    DetailRealEstateComponent,
    UserInvestmentsComponent,
    DocumentationComponent,
    CardsRealEstateComponent,
    LoginComponent,
    KycComponent,
    NewProyectComponent,
    SetRolComponent,
    NewSaleSellerComponent,
    AddDividendsComponent,
    DocumentationGeneralComponent,
    SetDataSaleComponent,
    AddDividendsComponent,
    ProyectsSetDataComponent,
    ProyectSetDataComponent,
    UriYoutubeComponent,
    ProfileComponent,
    BuyThroughCompanyComponent,
    BuyBatchThroughCompanyComponent,
    InvestmentListComponent,
    SetWhitelistComponent,
    ForcedTransferComponent,
    TransferBatchComponent,
    TransferListComponent,
    SetPauseComponent,
    RevertPaymentsComponent,
    InfoDividendsComponent,
    BuyWithoutPayComponent,
    HideProyectComponent,
    DocsLegalComponent,
    ModalLoginComponent,
    ModalRegisterComponent,
    ClickTouchDirective,
    ModalKycComponent,
    KycStagesComponent,
    KycRegisterComponent,
    CustomDialogComponent,
    KycSurveyComponent,
    AdminWalletComponent,
    KycCompletedComponent,
    WithdrawalRequestsComponent,
    ModalPageComponent,
    RegisterPageComponent,
    ForgotPasswordComponent,
    FiltersBarComponent,
    KycFinalComponent,
    Verify2faComponent,
    ManageBonusesComponent,
    FailedPaymentsManagementComponent,
    FailedPaymentsFiltersComponent,
    FailedPaymentsTableComponent,
    AdminComponent,
    SetCreditCardFeeComponent
  ],
  imports: [
    BrowserModule,
    CommonModule,
    RouterModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    NgxJsonViewerModule,
    HttpClientModule,
    HammerModule,
    // Angular 17: Services are provided in root automatically
    // Standalone components are imported directly
    InvestmentTutorialsComponent,
    EditTutorialsComponent,
    EditReferralTutorialsComponent,
    NgbModule,
    NgbCollapseModule,
    // MDB modules removed - using Angular Material equivalents
    MatDialogModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    NgSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
    SharedModule,
    MatButtonModule,
    MatCardModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatOptionModule,
    MatRippleModule,
    SafePipe,
    ResidenceChangeModalModule,
    MainContainerComponent,
    MenuMovilComponent,
    MenuDesktopComponent,
    SpinnerComponent,
    NavbarUserComponent,
    SetKycComponent,
    BalanceResumeComponent,
    BalanceListComponent,
    ReservesManagementComponent,
    AdminFiscalDocumentationComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  providers: [
    ScreenTrackingService,
    UserTrackingService,
    AuthService,
    TwoFactorAuthService,
    { provide: FIREBASE_OPTIONS, useValue: environment.firebase },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MfaInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RetryInterceptor,
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
